import { createInfiniteQuery, createQuery } from "react-query-kit";

import { getApi } from "@api/apiPath";
import { axiosClient } from "@api/clients.ts";
import { infiniteHelper } from "@utils";

import type { Fetcher } from "react-query-kit";

import type { IApiResponse, IPagination, IPaginationParams } from "@api";
import type { IGetAllApiEntities } from "@api/apiPath";

export const createGetAllFunc = <
  TResponse,
  TPathParams extends object | undefined,
  TQueryParams extends IPaginationParams,
>(
  path: IGetAllApiEntities,
) => {
  type IVariables = {
    pathParam: TPathParams;
  } & TQueryParams;

  const { key, getPath } = getApi(`${path}.getAll`);

  const getAllFunc: Fetcher<TResponse, IVariables, number> = (
    { pathParam, ...restParam },
    { pageParam, signal },
  ) => {
    return axiosClient.get(getPath(pathParam as never), {
      params: { ...restParam, page: pageParam ?? restParam?.page },
      signal,
    });
  };

  return { getAllFunc, key };
};

export const createGetAllHook = <
  TResponse extends IApiResponse<IPagination<unknown, unknown>>,
  TVariables extends IPaginationParams,
>(
  key: string,
  getFunc: Fetcher<TResponse, TVariables, number>,
) => {
  const useGetAll = createQuery<TResponse, TVariables>({
    queryKey: [key],
    fetcher: getFunc,
  });

  const useGetAllInfinite = createInfiniteQuery<
    TResponse,
    TVariables,
    Error,
    number
  >({
    queryKey: [`infinite.${key}`],
    fetcher: getFunc,
    ...infiniteHelper,
  });

  return { useGetAll, useGetAllInfinite };
};

export const createGetAllApi = <
  TResponse extends IApiResponse<IPagination<unknown, unknown>>,
  TPathParams extends object | undefined,
  TQueryParams extends IPaginationParams,
>(
  path: IGetAllApiEntities,
) => {
  const { key, getAllFunc } = createGetAllFunc<
    TResponse,
    TPathParams,
    TQueryParams
  >(path);
  const { useGetAll, useGetAllInfinite } = createGetAllHook(key, getAllFunc);

  return { getAllFunc, useGetAll, useGetAllInfinite };
};
